<template>
  <div class="chart-wrapper" v-show="chart" ref="chartWrapper">
    <div ref="chart" data-qa="chart" :style="{ height: `${chartHeight}px` }"></div>
    <div v-if="legendSum?.active" class="legend-wrapper">
      <div class="legend">
        <template v-for="(seriesOptionsGroup, key, index) in seriesOptionsGrouped" :key="key">
          <template
            v-if="seriesOptionsGroup.length > 1 || seriesOptionsGroup[0]?.forceDropdownOnOne"
          >
            <rbz-dropdown
              :items="seriesOptionsGroupDropdownItems(key.toString(), seriesOptionsGroup)"
              :value="getSeriesGroupDisplayedItems(key.toString(), seriesOptionsGroup)"
              type="multi-select"
              :all-label="key.toString()"
              :all-disabled="!!groupedSeriesSearchValue"
              :all-checked-override="true"
              :all-checked-override-value="
                isSeriesGroupAllSelected(key.toString(), seriesOptionsGroup)
              "
              :all-checked-override-indeterminate="
                !isSeriesGroupAllSelected(key.toString(), seriesOptionsGroup) &&
                !isSeriesGroupNoneSelected(key.toString(), seriesOptionsGroup)
              "
              :toggle-value="groupedSeries.includes(key.toString())"
              :toggle-disabled="isSeriesGroupToggleDisabled(key.toString(), seriesOptionsGroup)"
              @update:value="
                setSeriesGroupDisplay(key.toString(), seriesOptionsGroup, $event as any)
              "
              @update:searchValue="groupedSeriesSearchValue = $event"
              @is-active="legendDropdownActiveChange($event)"
              @update:toggleValue="toggleAggregatedGroup(key.toString())"
              :add-checked-missing-values="false"
              :toggle-label="t('dashboard.charts.aggregated')"
              :class="
                isSeriesGroupNoneSelectedFrozen(key.toString(), seriesOptionsGroup) ? 'hidden' : ''
              "
              class="legend-dropdown"
            >
              <template #headerContentOverride="props">
                <template
                  v-if="
                    frozenGroupedSeries.includes(key.toString()) ||
                    isSeriesGroupNoneSelectedFrozen(key.toString(), seriesOptionsGroup)
                  "
                >
                  <rbz-chart-legend-item
                    :title="key.toString()"
                    :sum="getSeriesGroupSum(key.toString())?.toString()"
                    :show="frozenDisplayedSeries.includes(key.toString())"
                    :color="getSeriesStyle(seriesOptionsGroup[0].key).color"
                    :series-style="getSeriesStyle(seriesOptionsGroup[0].key).style"
                    data-qa="chart-legend-item"
                    @mouseover="setFocusedSeries(key.toString())"
                    @mouseleave="setFocusedSeries(null)"
                  />
                </template>
                <template v-else>
                  <template v-for="seriesKey in frozenDisplayedSeries" :key="seriesKey">
                    <rbz-chart-legend-item
                      v-if="isSeriesInDropdown(props.items as any, seriesKey)"
                      :title="getSeriesFromKey(seriesKey)?.title"
                      :subtitle="getSeriesFromKey(seriesKey)?.subtitle"
                      :sum="getSeriesSum(getSeriesFromKey(seriesKey))?.toString()"
                      :show="frozenDisplayedSeries.includes(seriesKey)"
                      :color="getSeriesStyle(seriesKey).color"
                      :series-style="getSeriesStyle(seriesKey).style"
                      data-qa="chart-legend-item"
                      @mouseover="setFocusedSeries(seriesKey)"
                      @mouseleave="setFocusedSeries(null)"
                    />
                  </template>
                </template>
              </template>
            </rbz-dropdown>
          </template>
          <template v-else>
            <rbz-chart-legend-item
              :title="seriesOptionsGroup[0].title"
              :subtitle="seriesOptionsGroup[0].subtitle"
              :sum="getSeriesSum(seriesOptionsGroup[0])?.toString()"
              :show="displayedSeries.includes(seriesOptionsGroup[0].key)"
              :color="getSeriesStyle(seriesOptionsGroup[0].key).color"
              :series-style="getSeriesStyle(seriesOptionsGroup[0].key).style"
              @click="
                toggleSeriesDisplay(seriesOptionsGroup[0]);
                updateSeries();
              "
              @mouseover="setFocusedSeries(seriesOptionsGroup[0].key)"
              @mouseleave="setFocusedSeries(null)"
              class="single-item"
              data-qa="chart-legend-item"
            />
          </template>
          <div class="vertical-divider" v-if="index < Object.keys(seriesOptionsGrouped).length - 1">
            <layout-divider-line orientation="vertical" color="light-gray" />
          </div>
        </template>
      </div>
      <div class="control-buttons">
        <form-button
          variant="tertiary"
          data-qa="reset-filter-button"
          :label="t('dashboard.charts.reset')"
          :disabled="isFilterInInitialState()"
          @button-click="setFilterToInitialState()"
          :ignore-update-in-process="true"
        />
        <form-button
          variant="tertiary"
          data-qa="unselect-all-button"
          :label="t('dashboard.charts.unselectAll')"
          :disabled="isFilterInEmptyState()"
          @button-click="setFilterToEmptyState()"
          :ignore-update-in-process="true"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createVNode, defineComponent, nextTick, PropType, render, VNode } from 'vue';
import _ from 'lodash';
import uPlot, { Cursor, Series } from 'uplot';
import { DropdownItem, GenericObject, RbzSeriesStyle } from '../../rbz/types';
import Utils from '../../helpers/Utils';
import DateTimeUtils from '../../helpers/DateTimeUtils';
import FormButton from '../../rbz/components/FormButton.vue';
import LayoutDividerLine from '../../rbz/components/LayoutDividerLine.vue';
import RbzChartLegendItem from '../../rbz/components/RbzChartLegendItem.vue';
import FormDropdown from '../../rbz/components/FormDropdown.vue';
import IconCircle from '../../assets/icons/IconCircle.vue';
import ScaleKeyMatcher = uPlot.Cursor.Sync.ScaleKeyMatcher;
import { useI18n } from 'vue-i18n';

export type SeriesOptions = {
  key: string;
  title: string;
  subtitle?: string;
  fieldName: string;
  show: boolean;
  enforceShowInLegendTooltip?: boolean;
  drawStyle?: 'line' | 'bars' | 'spline';
  strokeColor?: Series.Stroke;
  group?: string;
  forceDropdownOnOne?: boolean;
};

export type GroupColorPalette = {
  [key: string]: string[];
};

export type LegendSumConfig = {
  active: boolean;
};

export type LegendAsTooltipConfig = {
  active: boolean;
  width?: string;
  enforceSide?: 'left' | 'right' | 'middle';
  showTopX?: {
    startIndex: number;
    sliceEnd?: number;
  };
  overrideTooltip?: boolean;
};

type SeriesLine = {
  tooltip: HTMLElement;
  vnode: VNode;
};

export default defineComponent({
  name: 'RbzChart',
  components: { FormButton, RbzDropdown: FormDropdown, RbzChartLegendItem, LayoutDividerLine },
  props: {
    data: Array as PropType<GenericObject[]>,
    seriesOptions: Array as PropType<SeriesOptions[]>,
    hiddenKeys: Array as PropType<string[]>,
    groupColorPalette: Object as PropType<GroupColorPalette>,
    legendSum: Object as PropType<LegendSumConfig>,
    legendAsTooltip: Object as PropType<LegendAsTooltipConfig>,
    displayFractionalAxisValues: Boolean,
    chartHeight: Number,
    syncKey: String,
  },
  emits: ['zoomed'],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      chart: null as uPlot | null,
      chartOptions: null as uPlot.Options | null,
      ctx: null as CanvasRenderingContext2D | null,

      zoomedBoundaries: null as { min: number; max: number } | null,
      displayedSeriesInitial: [] as string[],
      displayedSeries: [] as string[],
      groupedSeriesInitial: [] as string[],
      groupedSeries: [] as string[],
      groupedSeriesSearchValue: null as string | null,
      seriesToUpdate: [] as any[],
      frozenDisplayedSeries: [] as string[],
      frozenGroupedSeries: [] as string[],
      seriesCount: null as number | null,
      seriesGroups: {} as GenericObject,
      focusedSeriesIndex: null as number | null,
      legendSeriesTooltip: null as HTMLElement | null,
      setChartSizeInterval: null as ReturnType<typeof setInterval> | null,
    };
  },
  watch: {
    data: {
      handler: function (val, oldVal) {
        if (!val && !oldVal) {
          return;
        }
        this.zoomedBoundaries = null;
        this.redrawChart();
      },
      deep: true,
    },
    seriesOptions: {
      handler: function (val, oldVal) {
        if (!val && !oldVal) {
          return;
        }
        const zoomedBoundaries = _.cloneDeep(this.zoomedBoundaries);
        this.redrawChart();
        this.setChartZoom(zoomedBoundaries);
      },
      deep: true,
    },
    legendSum: {
      handler: function (val, oldVal) {
        if (!val && !oldVal) {
          return;
        }
        this.redrawChart();
      },
      deep: true,
    },
    hiddenKeys: {
      handler: function (val, oldVal) {
        if (!val && !oldVal) {
          return;
        }
        if (this.chart) {
          this.hideSeriesByHiddenKeys();
        }
      },
      deep: true,
    },
    chartHeight: {
      handler: function () {
        this.setChartSize();
      },
    },
  },
  computed: {
    isSeriesOptionsGrouped(): boolean {
      const filteredSeriesOptions = this.seriesOptions.filter((seriesOptions) => {
        return !this.hiddenKeys || !this.hiddenKeys.includes(seriesOptions.key);
      });
      return filteredSeriesOptions.some((seriesOptions) => {
        return seriesOptions.group;
      });
    },

    seriesOptionsGrouped(): GenericObject {
      const filteredSeriesOptions = this.seriesOptions.filter((seriesOptions) => {
        return !this.hiddenKeys || !this.hiddenKeys.includes(seriesOptions.key);
      });
      if (this.isSeriesOptionsGrouped) {
        const grouped = _.groupBy(filteredSeriesOptions, 'group');
        _.forEach(grouped['undefined'], (item) => {
          grouped[item.key] = [item];
        });
        delete grouped['undefined'];
        return grouped;
      } else {
        const ungroupedMap: Record<string, any[]> = {};
        _.forEach(filteredSeriesOptions, (item) => {
          ungroupedMap[item.key] = [item];
        });
        return ungroupedMap;
      }
    },

    precomputedSums(): Record<string, Record<string, number>> {
      const returnValue: Record<string, Record<string, number>> = {};
      this.data.forEach((entry: any) => {
        const timeframe = entry.timeframe;
        if (!returnValue[timeframe]) {
          returnValue[timeframe] = {};
        }

        const fieldSumMap = returnValue[timeframe];

        Object.keys(entry).forEach((fieldName) => {
          if (fieldName !== 'timeframe') {
            const value = Number(entry[fieldName]) || 0;
            fieldSumMap[fieldName] = (fieldSumMap[fieldName] || 0) + value;
          }
        });
      });
      return returnValue;
    },
  },
  methods: {
    addSeriesToChartOptions(focus: boolean): GenericObject {
      const groups: GenericObject = {};
      this.seriesOptions.forEach((seriesOptions, index) => {
        let groupItemsCount;
        if (this.isSeriesOptionsGrouped) {
          groupItemsCount = _.filter(this.seriesOptions, (item) => {
            return item.group === seriesOptions.group;
          }).length;
          if (seriesOptions.group && groupItemsCount > 1) {
            if (!Object.keys(groups).includes(seriesOptions.group)) {
              groups[seriesOptions.group] = [];
              this.chartOptions.series.push({
                key: seriesOptions.group,
                show: !focus,
                spanGaps: true,
                label: `${seriesOptions.group}${focus ? 'focus' : ''}`,
                stroke: () => {
                  return this.getSeriesStyle(seriesOptions.group).color;
                },
                width: focus ? 4 : 2.5,
                fill: (u: uPlot, seriesIdx: number) => {
                  const color = this.getSeriesStyle(seriesOptions.group).color;
                  return this.fillGradient(u, seriesIdx, color);
                },
                paths: this.paths as Series.PathBuilder,
                points: {
                  show: false,
                },
                seriesOptionsIndex: index,
                group: seriesOptions.group,
                focus: focus,
              } as any);
            }
            groups[seriesOptions.group].push(seriesOptions.fieldName);
          }
        }
        const showSeries = focus
          ? false
          : !seriesOptions.group || !groupItemsCount || groupItemsCount === 1
            ? seriesOptions.show
            : false;
        this.chartOptions.series.push({
          key: seriesOptions.key,
          show: showSeries,
          spanGaps: true,
          label: `${seriesOptions.key}${focus ? 'focus' : ''}`,
          stroke: () => {
            return this.getSeriesStyle(seriesOptions.key).color;
          },
          width: focus ? 4 : 2.5,
          fill: seriesOptions.group
            ? null
            : (u: uPlot, seriesIdx: number) => {
                const color = this.getSeriesStyle(seriesOptions.key).color;
                return this.fillGradient(u, seriesIdx, color);
              },
          paths: this.paths as Series.PathBuilder,
          points: {
            show: false,
          },
          seriesOptionsIndex: index,
          focus: focus,
        } as any);
      });
      return groups;
    },

    generateDatasets() {
      const datasets: [xValues: number[], ...yValues: (number | null | undefined)[][]] = [[]];
      for (let i = 1; i < this.chartOptions.series.length; i++) {
        datasets.push([]); // skipping first series as it's xValues which we added above
      }
      const sortedGroupedData = _.groupBy(_.sortBy(this.data, 'timeframe'), 'timeframe');
      _.forEach(sortedGroupedData, (dataItem) => {
        const timeframe = dataItem[0].timeframe;
        datasets[0].push(timeframe); // timestamps X axis
        datasets.forEach((dataset, index) => {
          if (index !== 0) {
            // timestamps X axis
            let value = 0;
            const series: any = this.chartOptions.series[index];
            const group = series.group;
            if (group) {
              _.forEach(this.seriesGroups[group], (seriesFieldName) => {
                const seriesOptions = this.seriesOptions.find((seriesOptions) => {
                  return (
                    seriesOptions.group === group && seriesOptions.fieldName === seriesFieldName
                  );
                });
                if (!this.hiddenKeys || !this.hiddenKeys.includes(seriesOptions.key)) {
                  value += this.precomputedSums[timeframe]?.[seriesFieldName] || 0;
                }
              });
            } else {
              const seriesOptionsIndex = series.seriesOptionsIndex;
              const fieldName = this.seriesOptions[seriesOptionsIndex].fieldName;
              value = this.precomputedSums[timeframe]?.[fieldName];
              if (_.isNil(value)) {
                value = null;
              }
            }
            dataset.push(value);
          }
        });
      });
      return datasets;
    },

    redrawChart() {
      // Cursor sync over multiple charts
      const sync = uPlot.sync(this.syncKey);
      const matchSyncKeys: ScaleKeyMatcher = (own: string | null, ext: string | null) => {
        return own === ext;
      };
      const cursorOpts: Cursor = {
        sync: {
          key: sync.key,
          match: [matchSyncKeys, matchSyncKeys],
        },
      };
      this.chartOptions = {
        class: 'chart',
        width: 0,
        height: 0,
        plugins: [],
        cursor: cursorOpts,
        tzDate: (ts) => uPlot.tzDate(new Date(ts), 'Etc/UTC'),
        ms: 1,
        focus: {
          alpha: 0.3,
        },
        series: [
          {
            value: (self, ts) => {
              let timeFormat;
              if (Math.floor(ts / 60000) * 60 === ts / 1000) {
                timeFormat = uPlot.fmtDate('{YYYY}-{MM}-{DD} {HH}:{mm}');
              } else if (Math.floor(ts / 1000) === ts / 1000) {
                timeFormat = uPlot.fmtDate('{YYYY}-{MM}-{DD} {HH}:{mm}:{ss}');
              } else {
                timeFormat = uPlot.fmtDate('{YYYY}-{MM}-{DD} {HH}:{mm}:{ss}.{fff}');
              }
              return timeFormat(DateTimeUtils.adjustDateToUTC(new Date(ts)));
            },
          }, // timestamps X axis
        ],
        axes: [
          {
            grid: {
              show: false,
            },
            ticks: {
              show: false,
            },
            values: [
              [
                3600 * 24 * 365 * 1000, // tick incr - year
                '{MM}-{DD}', // default
                '{YYYY}-{MM}-{DD}', // year
                null, // month
                null, // day
                null, // hour
                null, // minute
                null, // second
                0, // mode
              ],
              [
                3600 * 24 * 28 * 1000, // tick incr - month
                '{MM}-{DD}', // default
                '{YYYY}-{MM}-{DD}', // year
                null, // month
                null, // day
                null, // hour
                null, // minute
                null, // second
                0, // mode
              ],
              [
                3600 * 24 * 1000, // tick incr - day
                '{MM}-{DD}', // default
                '{YYYY}-{MM}-{DD}', // year
                null, // month
                null, // day
                null, // hour
                null, // minute
                null, // second
                0, // mode
              ],
              [
                3600 * 1000, // tick incr - hour
                '{HH}:{mm}', // default
                '{YYYY}-{MM}-{DD}', // year
                '{MM}-{DD}', // month
                '{MM}-{DD}', // day
                null, // hour
                null, // minute
                null, // second
                0, // mode
              ],
              [
                60 * 1000, // tick incr - minute
                '{HH}:{mm}', // default
                '{YYYY}-{MM}-{DD}', // year
                '{MM}-{DD}', // month
                '{MM}-{DD}', // day
                null, // hour
                null, // minute
                null, // second
                0, // mode
              ],
              [
                1000, // tick incr - second
                ':{ss}', // default
                '{YYYY}-{MM}-{DD}', // year
                '{MM}-{DD}', // month
                '{MM}-{DD}', // day
                '{HH}:{mm}', // hour
                '{HH}:{mm}', // minute
                null, // second
                0, // mode
              ],
              [
                0.001 * 1000, // tick incr - millisecond
                ':{ss}.{fff}', // default
                '{YYYY}-{MM}-{DD}', // year
                '{MM}-{DD}', // month
                '{MM}-{DD}', // day
                '{HH}:{mm}:{ss}', // hour
                '{HH}:{mm}:{ss}', // minute
                '{HH}:{mm}:{ss}', // second
                0, // mode
              ],
            ],
          },
          {
            show: true,
            grid: {
              show: false,
            },
            ticks: {
              show: false,
            },
            size(self, values, axisIdx, cycleNum) {
              const axis = self.axes[axisIdx];

              // bail out, force convergence
              if (cycleNum > 1) {
                // Casting to any as _size is a private variable and isn't listed in the type
                return (axis as any)._size;
              }

              let axisSize = axis.ticks.size + axis.gap;

              // find longest value
              const longestVal = (values ?? []).reduce(
                (acc, val) => (val?.length > acc?.length ? val : acc),
                ''
              );

              if (longestVal != '') {
                self.ctx.font = axis.font[0];
                axisSize += self.ctx.measureText(longestVal).width / devicePixelRatio;
              }

              return Math.max(Math.ceil(axisSize), 35);
            },
            values: (self, splits) => {
              return splits.map((value) => {
                if (this.displayFractionalAxisValues) {
                  return value.toString();
                } else {
                  return value === null || value % 1 !== 0
                    ? null
                    : this.amountSuffixFormatter(value);
                }
              });
            },
          },
        ],
        scales: {
          y: {
            range: (u, dataMin, dataMax) => {
              if (dataMin == null && dataMax == null) {
                return [0, 100];
              }
              return uPlot.rangeNum(dataMin, dataMax, 0.1, true);
            },
          },
        },
        hooks: {
          setScale: [
            (u, key) => {
              if (
                key === 'x' &&
                u.status === 1 &&
                _.isFinite(u.scales['x'].min) &&
                _.isFinite(u.scales['x'].max) &&
                (u.series[0].min !== u.scales['x'].min || u.series[0].max !== u.scales['x'].max)
              ) {
                this.zoomedBoundaries = {
                  min: u.scales['x'].min,
                  max: u.scales['x'].max,
                };
                this.$emit('zoomed', this.zoomedBoundaries);
              }
            },
          ],
        },
      };
      this.seriesGroups = this.addSeriesToChartOptions(false);
      this.seriesCount = this.chartOptions.series.length - 1;
      if (this.legendSum?.active) {
        this.addSeriesToChartOptions(true);
      }
      const datasets = this.generateDatasets();
      if (this.legendAsTooltip?.active) {
        this.chartOptions.plugins.push(this.legendAsTooltipPlugin());
      }
      if (this.chart) {
        this.legendSeriesTooltip?.remove();
        this.chart.setData([]);
        this.chart.destroy();
      }
      if (!this.ctx) {
        const canvas = document.createElement('canvas');
        this.ctx = canvas.getContext('2d');
      }

      this.chart = new uPlot(this.chartOptions, datasets, this.$refs.chart as HTMLElement);
      this.displayedSeriesInitial = _.map(
        _.filter(this.chart.series, (series) => {
          return series.show && series.scale === 'y';
        }),
        'label'
      ) as string[];
      this.displayedSeries = _.cloneDeep(this.displayedSeriesInitial);
      this.frozenDisplayedSeries = _.cloneDeep(this.displayedSeries);
      this.groupedSeriesInitial = Object.keys(this.seriesGroups);
      this.groupedSeries = _.cloneDeep(this.groupedSeriesInitial);
      this.frozenGroupedSeries = _.cloneDeep(this.groupedSeries);
      sync.sub(this.chart);
      this.hideSeriesByHiddenKeys();
      // Pushing the resize action to the end of queue in order for the new chart to be rendered beforehand
      nextTick(() => {
        this.setChartSize();
      });
    },

    legendAsTooltipPlugin() {
      let overElement: HTMLDivElement;
      let seriesLine: SeriesLine[];
      let previousIndex: number;

      const init = (uPlotChart: uPlot, opts: uPlot.Options) => {
        const legendElement = uPlotChart.root.querySelector('.u-legend');
        legendElement.remove();

        overElement = uPlotChart.over;
        this.legendSeriesTooltip = document.createElement('div');
        this.legendSeriesTooltip.classList.add('legend-tooltip');
        document.body.appendChild(this.legendSeriesTooltip);
        seriesLine = opts.series.map((series: any, index: number) => {
          const tooltip = document.createElement('div');
          let vnode;
          if (index === 0) {
            tooltip.className = 'tooltip-line tooltip-time-line';
            const seriesDate = document.createElement('div');
            seriesDate.className = 'series-date rbz-text-small';
            tooltip.appendChild(seriesDate);
            const seriesTime = document.createElement('div');
            seriesTime.className = 'series-time rbz-text-small';
            tooltip.appendChild(seriesTime);
          } else {
            tooltip.className = 'tooltip-line';
            this.legendSeriesTooltip.appendChild(tooltip);
            vnode = createVNode(IconCircle, {
              color: series.strokeColor,
              circleStyle: 'full',
              bypassDefaultColor: true,
            });
            render(vnode, tooltip);
            const seriesTitle = document.createElement('div');
            seriesTitle.className = 'series-title rbz-text-small';
            tooltip.appendChild(seriesTitle);
            const seriesValue = document.createElement('div');
            seriesValue.className = 'series-value rbz-text-regular';
            tooltip.appendChild(seriesValue);
          }
          return {
            tooltip,
            vnode,
          };
        });

        const hideTips = () => {
          this.legendSeriesTooltip.style.display = 'none';
          previousIndex = null;
        };

        const showTips = () => {
          this.legendSeriesTooltip.style.display = null;
          seriesLine.forEach(({ tooltip }: SeriesLine, index: number) => {
            if (index === 0) {
              tooltip.style.display = null;
            }
            tooltip.style.display = uPlotChart.series[index].show ? null : 'none';
          });
        };

        overElement.addEventListener('mouseleave', () => {
          hideTips();
        });
        overElement.addEventListener('mouseenter', () => {
          showTips();
        });
        if (uPlotChart.cursor.left < 0) {
          hideTips();
        } else {
          showTips();
        }
      };

      const updateLegendContent = (uPlotChart: uPlot) => {
        const { idx } = uPlotChart.cursor;
        if (this.legendSeriesTooltip.style.display === 'none' || _.isNil(idx)) {
          return;
        }
        // value
        const seriesToAdd: HTMLElement[] = [];
        seriesLine.forEach(({ tooltip }: SeriesLine, index: number) => {
          if (this.legendAsTooltip.showTopX) {
            seriesToAdd.push(tooltip);
            const originalIndexClass = tooltip.className.split(' ').find((className) => {
              return className.startsWith('original-index-');
            });
            let originalIndex: number;
            if (_.isNil(originalIndexClass)) {
              originalIndex = index;
              tooltip.classList.add(`original-index-${index}`);
            } else {
              originalIndex = Number(originalIndexClass.split('-').pop());
            }
            index = originalIndex;
          }
          const value: number | string = uPlotChart.data[index][idx];
          if (index === 0) {
            const date = new Date(value);
            const year = date.getUTCFullYear();
            const month =
              date.getUTCMonth() + 1 < 10 ? `0${date.getUTCMonth() + 1}` : date.getUTCMonth() + 1;
            const day = date.getUTCDate() < 10 ? `0${date.getUTCDate()}` : date.getUTCDate();
            const dateString = `${year}-${month}-${day}`;
            const hours = date.getUTCHours() < 10 ? `0${date.getUTCHours()}` : date.getUTCHours();
            const minutes =
              date.getUTCMinutes() < 10 ? `0${date.getUTCMinutes()}` : date.getUTCMinutes();
            const seconds =
              date.getUTCSeconds() < 10 ? `0${date.getUTCSeconds()}` : date.getUTCSeconds();
            const timeString = `${hours}:${minutes}:${seconds}`;
            const dateElement = tooltip.getElementsByClassName('series-date')[0];
            const timeElement = tooltip.getElementsByClassName('series-time')[0];
            dateElement.textContent = `${dateString}`;
            timeElement.textContent = `${timeString}`;
          } else {
            if (_.isNil(value)) {
              tooltip.style.display = 'none';
              return;
            }
            const enforceShow = this.seriesOptions[index - 1]?.enforceShowInLegendTooltip;
            if (uPlotChart.series[index].show || enforceShow) {
              tooltip.style.display = 'flex';
              if (enforceShow) {
                tooltip.classList.remove('u-off');
              }
            } else {
              tooltip.style.display = 'none';
            }
            const titleElement = tooltip.getElementsByClassName('series-title')[0] as HTMLElement;
            const valueElement = tooltip.getElementsByClassName('series-value')[0] as HTMLElement;
            titleElement.textContent = uPlotChart.series[index].label as string;
            valueElement.textContent = `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        });
        if (this.legendAsTooltip.showTopX) {
          const start = this.legendAsTooltip.showTopX.startIndex;
          while (this.legendSeriesTooltip.firstChild) {
            this.legendSeriesTooltip.removeChild(this.legendSeriesTooltip.lastChild);
          }
          const seriesToKeep: HTMLElement[] = seriesToAdd.slice(0, start);
          let seriesToSort: HTMLElement[] = seriesToAdd.slice(start, seriesToAdd.length);
          seriesToSort = _.orderBy(
            seriesToSort,
            (seriesElement) => {
              const valueElement = seriesElement.getElementsByClassName('series-value')[0];
              const valueString = valueElement.textContent;
              return Number(valueString.replace(/,/g, ''));
            },
            'desc'
          );
          const sliceEnd = this.legendAsTooltip.showTopX.sliceEnd || seriesToSort.length;
          let displayedSeriesCounter = 0;
          seriesToKeep.forEach((seriesElement) => {
            this.legendSeriesTooltip.appendChild(seriesElement);
          });
          seriesToSort.forEach((seriesElement) => {
            if (displayedSeriesCounter >= sliceEnd) {
              return; // continue to next forEach iteration
            }
            if (seriesElement.style.display !== 'none') {
              displayedSeriesCounter++;
            }
            this.legendSeriesTooltip.appendChild(seriesElement);
          });
        }
      };

      const updateLegendColors = (uPlotChart: uPlot) => {
        const { idx } = uPlotChart.cursor;
        if (this.legendSeriesTooltip.style.display === 'none' || _.isNil(idx)) {
          return;
        }
        // value
        seriesLine.forEach(({ tooltip, vnode }, index) => {
          if (index > 0) {
            const colorElement = tooltip.getElementsByClassName('icon-circle')[0];
            const label = uPlotChart.series[index].label as string;
            colorElement.setAttribute('fill', 'none');
            colorElement.setAttribute('stroke', 'none');
            const fillOrStrokeSVG = this.getSeriesStyle(label).style === 'line' ? 'stroke' : 'fill';
            colorElement.setAttribute(
              fillOrStrokeSVG,
              (uPlotChart.series[index] as any).stroke(uPlotChart, index)?.toString()
            );
            vnode.component.props.circleStyle = this.getSeriesStyle(label).style;
          }
        });
      };

      const updateLegend = (uPlotChart: uPlot) => {
        const { idx } = uPlotChart.cursor;
        if (this.legendSeriesTooltip.style.display === 'none' || _.isNil(idx)) {
          return;
        }
        // content + color
        if (previousIndex !== idx) {
          updateLegendContent(uPlotChart);
          updateLegendColors(uPlotChart);
          previousIndex = idx;
        }
        // position
        let max: number;
        let maxIndex = 0;
        for (let index = 0; index < seriesLine.length; index++) {
          if (index === 0 || !uPlotChart.series[index].show) {
            continue;
          }
          const value = uPlotChart.data[index][idx];
          if (!max || max < value) {
            max = value;
            maxIndex = index;
          }
        }
        const xVal = uPlotChart.data[0][idx];
        const yVal = uPlotChart.data[maxIndex][idx];
        const xPos = Math.round(uPlotChart.valToPos(xVal, 'x'));
        const yPos = Math.round(uPlotChart.valToPos(yVal, uPlotChart.series[maxIndex].scale));
        const overLeft = overElement.getBoundingClientRect().left;
        const overTop = overElement.getBoundingClientRect().top;
        const height = this.legendSeriesTooltip.clientHeight;
        // position is not accurate as it's a canvas, moving 1px aside looks better
        const tooltipLeft = overLeft + xPos - 1;
        const tooltipTop = overTop + yPos - height - 10;
        // set position
        this.legendSeriesTooltip.style.left = `${tooltipLeft}px`;
        this.legendSeriesTooltip.style.top = `${tooltipTop}px`;
        this.legendSeriesTooltip.style.transform = 'translateX(-50%)';
        this.legendSeriesTooltip.style.setProperty('--arrow-left', '50%');
        let alteredTooltipLeft = tooltipLeft;
        // check margin from edge of screen - right
        const distanceFromEdgeRight =
          window.innerWidth - this.legendSeriesTooltip.getBoundingClientRect().right;
        if (distanceFromEdgeRight < 20) {
          alteredTooltipLeft = alteredTooltipLeft - (20 - distanceFromEdgeRight);
        }
        // check margin from edge of screen - left
        const distanceFromEdgeLeft = this.legendSeriesTooltip.getBoundingClientRect().left;
        if (distanceFromEdgeLeft < 20) {
          alteredTooltipLeft = alteredTooltipLeft + (20 - distanceFromEdgeLeft);
        }
        // set position again in case changed to not be off-screen
        if (alteredTooltipLeft !== tooltipLeft) {
          this.legendSeriesTooltip.style.left = `${alteredTooltipLeft}px`;
          this.legendSeriesTooltip.style.transform = 'translateX(-50%)';
          const movedDistance = tooltipLeft - alteredTooltipLeft;
          this.legendSeriesTooltip.style.setProperty(
            '--arrow-left',
            `calc(50% + ${movedDistance}px)`
          );
        }
      };

      return {
        hooks: {
          init,
          setCursor: updateLegend,
          setLegend: updateLegendColors,
        },
      };
    },

    paths(uPlotChart: uPlot, seriesIdx: number, idx0: number, idx1: number) {
      const { linear, bars, spline } = uPlot.paths;
      const series: any = this.chartOptions.series[seriesIdx];
      const seriesOptionsIndex = series.seriesOptionsIndex;
      const style = this.seriesOptions[seriesOptionsIndex]?.drawStyle;
      if (!style) {
        return;
      }
      let renderer;
      if (style === 'line') {
        renderer = linear();
      }
      if (style === 'bars') {
        renderer = bars({ size: [1, Infinity, 1] });
      }
      if (style === 'spline') {
        renderer = spline();
      }
      return renderer(uPlotChart, seriesIdx, idx0, idx1);
    },

    scaleGradient(
      u: uPlot,
      scaleKey: string,
      ori: number,
      scaleStops: [number, string][],
      discrete = false
    ): string | CanvasGradient {
      const scale = u.scales[scaleKey];

      // we want the stop below or at the scaleMax
      // and the stop below or at the scaleMin, else the stop above scaleMin
      let minStopIdx;
      let maxStopIdx;

      for (let i = 0; i < scaleStops.length; i++) {
        const stopVal = scaleStops[i][0];

        if (stopVal <= scale.min || _.isNil(minStopIdx)) {
          minStopIdx = i;
        }

        maxStopIdx = i;

        if (stopVal >= scale.max) {
          break;
        }
      }

      if (minStopIdx === maxStopIdx) {
        return scaleStops[minStopIdx][1];
      }

      let minStopVal = scaleStops[minStopIdx][0];
      let maxStopVal = scaleStops[maxStopIdx][0];

      if (minStopVal === -Infinity) {
        minStopVal = scale.min;
      }

      if (maxStopVal === Infinity) {
        maxStopVal = scale.max;
      }

      const minStopPos = u.valToPos(minStopVal, scaleKey, true);
      const maxStopPos = u.valToPos(maxStopVal, scaleKey, true);

      const range = minStopPos - maxStopPos;

      let x0;
      let y0;
      let x1;
      let y1;

      if (ori === 1) {
        x0 = x1 = 0;
        y0 = minStopPos;
        y1 = maxStopPos;
      } else {
        y0 = y1 = 0;
        x0 = minStopPos;
        x1 = maxStopPos;
      }

      const grd = this.ctx.createLinearGradient(x0, y0, x1, y1);

      let prevColor;

      for (let i = minStopIdx; i <= maxStopIdx; i++) {
        const s = scaleStops[i];

        const stopPos =
          i === minStopIdx
            ? minStopPos
            : i === maxStopIdx
              ? maxStopPos
              : u.valToPos(s[0], scaleKey, true);
        const pct = range ? (minStopPos - stopPos) / range : 0;

        if (discrete && i > minStopIdx) {
          grd.addColorStop(pct, prevColor);
        }

        grd.addColorStop(pct, (prevColor = s[1]));
      }

      return grd;
    },

    fillGradient(u: uPlot, seriesIdx: number, color: string): string | CanvasGradient {
      const series = u.series[seriesIdx];

      // get in-view y range for this series
      const min = Math.min(Infinity, series.min);
      const max = Math.max(-Infinity, series.max);
      const range = max - min;

      const rgbColor = Utils.hexToRgbArray(color);
      if (rgbColor) {
        return this.scaleGradient(u, series.scale, 1, [
          [min, `rgba(${rgbColor.join(', ')}, 0)`],
          [min + range, `rgba(${rgbColor.join(', ')}, 0.4)`],
        ]);
      } else {
        return 'rgba(0, 0, 0, 0)';
      }
    },

    // Programmatically change the width of the chart based on component width once loaded
    setChartSize() {
      this.setChartSizeInterval = setInterval(() => {
        const wrapperElement = this.$refs.chartWrapper as HTMLElement;
        if (wrapperElement && wrapperElement.clientWidth && this.chart) {
          this.chart.setSize({
            width: wrapperElement.clientWidth,
            height: this.chartHeight,
          });
          clearInterval(this.setChartSizeInterval as ReturnType<typeof setInterval>);
        }
      }, 50);
    },

    // Programmatically change the zoom of the chart based on zoom before seriesOptions changes
    setChartZoom(zoomedBoundaries: { min: number; max: number }) {
      const wrapperElement = this.$refs.chartWrapper;
      if (wrapperElement && this.chart && zoomedBoundaries) {
        this.chart.setScale('x', zoomedBoundaries);
      }
    },

    hideSeriesByHiddenKeys() {
      let mixedGroup = false; // Group containing both hidden and displayed keys
      _.forEach(this.chart.series, (series: any, index: number) => {
        if (index === 0) {
          return;
        }
        let hide;
        if (series.group) {
          const groupItemsFieldNames = this.seriesGroups[series.group];
          hide =
            this.hiddenKeys &&
            !_.some(groupItemsFieldNames, (itemFieldName) => {
              const seriesOptions = this.seriesOptions.find((seriesOptions) => {
                return (
                  seriesOptions.group === series.group && seriesOptions.fieldName === itemFieldName
                );
              });
              return seriesOptions && !this.hiddenKeys.includes(seriesOptions.key);
            });
          mixedGroup =
            !hide &&
            _.some(groupItemsFieldNames, (itemFieldName) => {
              const seriesOptions = this.seriesOptions.find((seriesOptions) => {
                return (
                  seriesOptions.group === series.group && seriesOptions.fieldName === itemFieldName
                );
              });
              return seriesOptions && !this.hiddenKeys.includes(seriesOptions.key);
            });
        } else {
          hide = this.hiddenKeys && this.hiddenKeys?.includes(series.key);
        }
        if (hide) {
          this.chart.setSeries(index, { show: false });
        } else {
          this.chart.setSeries(index, { show: this.displayedSeries.includes(series.label) });
        }
      });
      // reset chart data if group aggregated data changed
      if (mixedGroup) {
        const datasets = this.generateDatasets();
        this.chart.setData(datasets);
      }
    },

    amountSuffixFormatter(value: number) {
      return Utils.amountSuffixFormatter(value, this.t);
    },

    getSeriesFromKey(key: SeriesOptions['key']) {
      return this.seriesOptions.find((item) => {
        return item.key === key;
      });
    },

    getSeriesSum(seriesOptions: SeriesOptions) {
      return _.sumBy(this.data, seriesOptions.fieldName);
    },

    getSeriesGroupSum(seriesGroup: string) {
      const groupItems = this.seriesOptions.filter((seriesOptions) => {
        return seriesOptions.group === seriesGroup && !this.hiddenKeys?.includes(seriesOptions.key);
      });
      return _.sumBy(this.data, (item) => {
        let value = 0;
        _.forEach(groupItems, (groupItem) => {
          if (item[groupItem.fieldName]) {
            value += item[groupItem.fieldName];
          }
        });
        return value;
      });
    },

    getSeriesStyle(seriesKey: SeriesOptions['key']): { color: string; style: RbzSeriesStyle } {
      const styleCycle: RbzSeriesStyle[] = ['line', 'dash', 'dot'];
      const seriesOptions = this.seriesOptions.find((item) => {
        return item.key === seriesKey;
      });
      let color: string;
      let style: RbzSeriesStyle;
      if (seriesOptions) {
        if (this.isSeriesOptionsGrouped) {
          const sameGroupSeries = this.frozenDisplayedSeries.filter((label) => {
            return _.some(this.seriesOptions, (item) => {
              return item.key === label && item.group === seriesOptions.group;
            });
          });
          let groupIndex = sameGroupSeries.findIndex((item) => {
            return item === seriesKey;
          });
          const palette = this.groupColorPalette?.[seriesOptions.group] || null;
          if (palette) {
            groupIndex = Math.max(0, groupIndex);
            const paletteCycle = groupIndex % palette.length;
            const paletteIndex = Math.floor(groupIndex / palette.length) % palette.length;
            const isGrouped = this.frozenGroupedSeries.includes(seriesOptions.group);
            color = palette[paletteIndex];
            style = isGrouped ? 'full' : styleCycle[paletteCycle];
          }
        }
        if (!color || !style) {
          color = seriesOptions.strokeColor as string;
          style = 'full';
        }
      } else {
        color = this.groupColorPalette?.[seriesKey]?.[0];
        style = 'full';
      }
      return {
        color,
        style,
      };
    },

    updateSeries() {
      this.frozenGroupedSeries = _.cloneDeep(this.groupedSeries);
      this.frozenDisplayedSeries = _.cloneDeep(this.displayedSeries);
      if (!this.seriesToUpdate.length) {
        return;
      }
      _.forEach(this.seriesToUpdate, (item) => {
        const series = this.chart.series[item.seriesIndex];
        const focusSeries = _.findLast(this.chart.series, (focusSeries) => {
          return focusSeries.label === `${series.label}focus`;
        });
        series.show = item.show
          ? !this.hiddenKeys || !this.hiddenKeys.includes(item.seriesKey)
          : false;
        if (this.getSeriesStyle(item.seriesKey).style === 'line') {
          delete series.dash;
          delete focusSeries.dash;
        }
        if (this.getSeriesStyle(item.seriesKey).style === 'dash') {
          series.dash = [14, 9];
          focusSeries.dash = [14, 9];
        }
        if (this.getSeriesStyle(item.seriesKey).style === 'dot') {
          series.dash = [3.5, 3.5];
          focusSeries.dash = [3.5, 3.5];
        }
      });
      this.chart.redraw();
      this.seriesToUpdate = [];
    },

    setSeriesDisplay(seriesKey: SeriesOptions['key'], value: boolean) {
      const seriesIndex = _.findIndex(this.chart.series, (series: uPlot.Series) => {
        return series.label === seriesKey;
      });
      if (seriesIndex === -1) {
        return;
      }
      const series = this.chart.series[seriesIndex];
      if (!series) {
        return;
      }
      if (value) {
        if (!this.displayedSeries.includes(series.label as string)) {
          this.displayedSeries.push(series.label as string);
        }
      } else {
        this.displayedSeries = _.filter(this.displayedSeries, (seriesLabel) => {
          return seriesLabel !== (series.label as string);
        });
      }
      this.seriesToUpdate.push({
        seriesKey,
        seriesIndex,
        show: value,
      });
    },

    setSeriesGroupDisplay(
      seriesGroup: string,
      seriesOptions: SeriesOptions[],
      selectedSeries: SeriesOptions['key'][]
    ) {
      if (this.groupedSeries.includes(seriesGroup)) {
        // aggregated
        this.setSeriesDisplay(seriesGroup, !!selectedSeries.length);
      } else {
        // not aggregated
        _.forEach(seriesOptions, (series) => {
          const value = selectedSeries.includes(series['key']);
          this.setSeriesDisplay(series['key'], value);
        });
      }
    },

    getSeriesGroupDisplayedItems(seriesGroup: string, seriesOptions: SeriesOptions[]) {
      const seriesOptionsKeys = _.map(seriesOptions, 'key');
      if (this.displayedSeries.includes(seriesGroup)) {
        return seriesOptionsKeys;
      }
      return _.filter(this.displayedSeries, (seriesLabel) => {
        return seriesOptionsKeys.includes(seriesLabel);
      });
    },

    isSeriesGroupAllSelected(seriesGroup: string, seriesOptions: SeriesOptions[]) {
      const isGrouped = this.groupedSeries.includes(seriesGroup);
      const seriesOptionsKeys = _.map(seriesOptions, 'key');
      const difference = _.difference(seriesOptionsKeys, this.displayedSeries);
      const isAllSelected = difference.length === 0;
      return (
        (isGrouped && this.displayedSeries.includes(seriesGroup)) || (!isGrouped && isAllSelected)
      );
    },

    isSeriesGroupNoneSelected(seriesGroup: string, seriesOptions: SeriesOptions[]) {
      const isGrouped = this.groupedSeries.includes(seriesGroup);
      const seriesOptionsKeys = _.map(seriesOptions, 'key');
      const difference = _.difference(seriesOptionsKeys, this.displayedSeries);
      const isNoneSelected = difference.length === seriesOptionsKeys.length;
      return (
        (isGrouped && !this.displayedSeries.includes(seriesGroup)) || (!isGrouped && isNoneSelected)
      );
    },

    isSeriesGroupToggleDisabled(seriesGroup: string, seriesOptions: SeriesOptions[]) {
      const isGrouped = this.groupedSeries.includes(seriesGroup);
      const seriesOptionsKeys = _.map(seriesOptions, 'key');
      const difference = _.difference(seriesOptionsKeys, this.displayedSeries);
      const isAllSelected = difference.length === 0;
      const isNoneSelected = difference.length === seriesOptionsKeys.length;
      const isSearchActive = this.groupedSeriesSearchValue;
      const isSingleValueDropdown =
        _.groupBy(this.seriesOptions, 'group')[seriesGroup].length === 1;
      return (
        !!isSearchActive ||
        (!isGrouped && !isAllSelected && !isNoneSelected) ||
        !!isSingleValueDropdown
      );
    },

    isSeriesInDropdown(items: DropdownItem[], series: string) {
      return _.some(items, (item: DropdownItem) => {
        return item.option[0] === series;
      });
    },

    isSeriesGroupNoneSelectedFrozen(seriesGroup: string, seriesOptions: SeriesOptions[]) {
      const isGrouped = this.frozenGroupedSeries.includes(seriesGroup);
      const seriesOptionsKeys = _.map(seriesOptions, 'key');
      const difference = _.difference(seriesOptionsKeys, this.frozenDisplayedSeries);
      const isNoneSelected = difference.length === seriesOptionsKeys.length;
      return (
        (isGrouped && !this.frozenDisplayedSeries.includes(seriesGroup)) ||
        (!isGrouped && isNoneSelected)
      );
    },

    seriesOptionsGroupDropdownItems(
      seriesGroup: string,
      seriesOptions: SeriesOptions[]
    ): DropdownItem[] {
      return seriesOptions.map((item) => {
        const subtitle = `${item.subtitle} (${this.getSeriesSum(item)})`.trim();
        return {
          option: [item.key, item.title, subtitle],
          disabled: this.groupedSeries.includes(seriesGroup),
        };
      });
    },

    toggleSeriesDisplay(seriesOptions: SeriesOptions) {
      const seriesIndex = _.findIndex(this.chart.series, (series) => {
        return series.label === seriesOptions.key;
      });
      const series = this.chart.series[seriesIndex];
      if (series) {
        this.setSeriesDisplay(seriesOptions.key, !series.show);
      }
    },

    toggleAggregatedGroup(seriesGroup: string) {
      const groupItems = this.seriesOptions.filter((seriesOptions) => {
        return seriesOptions.group === seriesGroup;
      });
      if (this.groupedSeries.includes(seriesGroup)) {
        this.groupedSeries = _.filter(this.groupedSeries, (item) => {
          return item !== seriesGroup;
        });
        _.forEach(groupItems, (groupItem) => {
          const value = this.displayedSeries.includes(seriesGroup);
          this.setSeriesDisplay(groupItem.key, value);
        });
        this.setSeriesDisplay(seriesGroup, false);
      } else {
        const seriesOptionsKeys = _.map(groupItems, 'key');
        const difference = _.difference(seriesOptionsKeys, this.displayedSeries);
        const isAllSelected = difference.length === 0;
        this.setSeriesDisplay(seriesGroup, isAllSelected);
        _.forEach(groupItems, (groupItem) => {
          this.setSeriesDisplay(groupItem.key, false);
        });
        this.groupedSeries.push(seriesGroup);
      }
    },

    legendDropdownActiveChange(newActive: boolean) {
      if (newActive) {
        this.groupedSeriesSearchValue = null;
      }
      this.updateSeries();
    },

    setFilterToInitialState() {
      if (!this.chart) {
        return;
      }
      this.displayedSeries = _.cloneDeep(this.displayedSeriesInitial);
      this.groupedSeries = _.cloneDeep(this.groupedSeriesInitial);
      _.forEach(this.chart.series, (series: any, index: number) => {
        const seriesOptions = _.find(this.seriesOptions, (seriesOptions) => {
          return seriesOptions.key === series.label;
        });
        if (series.group) {
          const groupItemsFieldNames = this.seriesGroups[series.group];
          const hidden = !_.some(groupItemsFieldNames, (itemFieldName) => {
            const seriesOptions = this.seriesOptions.find((seriesOptions) => {
              return (
                seriesOptions.group === series.group && seriesOptions.fieldName === itemFieldName
              );
            });
            return !this.hiddenKeys || !this.hiddenKeys.includes(seriesOptions.key);
          });
          this.chart.setSeries(index, { show: !hidden && !series.focus });
        } else if (seriesOptions && !this.groupedSeries.includes(seriesOptions.group)) {
          const hidden = series.key
            ? this.hiddenKeys && this.hiddenKeys.includes(series.key)
            : false;
          this.chart.setSeries(index, { show: !hidden && seriesOptions.show && !series.focus });
        } else {
          this.chart.setSeries(index, { show: false });
        }
      });
      this.updateSeries();
    },

    setFilterToEmptyState() {
      if (!this.chart) {
        return;
      }
      this.displayedSeries = [];
      this.groupedSeries = _.cloneDeep(this.groupedSeriesInitial);
      _.forEach(this.chart.series, (series, index) => {
        this.chart.setSeries(index, { show: false });
      });
      this.updateSeries();
    },

    isFilterInInitialState() {
      const isDisplayedSeriesEqual =
        _.differenceWith(this.frozenDisplayedSeries, this.displayedSeriesInitial, _.isEqual)
          .length === 0 &&
        _.differenceWith(this.displayedSeriesInitial, this.frozenDisplayedSeries, _.isEqual)
          .length === 0;
      const isGroupedSeriesEqual =
        _.differenceWith(this.frozenGroupedSeries, this.groupedSeriesInitial, _.isEqual).length ===
          0 &&
        _.differenceWith(this.groupedSeriesInitial, this.frozenGroupedSeries, _.isEqual).length ===
          0;
      return isDisplayedSeriesEqual && isGroupedSeriesEqual;
    },

    isFilterInEmptyState() {
      const isDisplayedSeriesEqual =
        _.differenceWith(this.frozenDisplayedSeries, [], _.isEqual).length === 0;
      const isGroupedSeriesEqual =
        _.differenceWith(this.frozenGroupedSeries, this.groupedSeriesInitial, _.isEqual).length ===
          0 &&
        _.differenceWith(this.groupedSeriesInitial, this.frozenGroupedSeries, _.isEqual).length ===
          0;
      return isDisplayedSeriesEqual && isGroupedSeriesEqual;
    },

    setFocusedSeries(seriesKey: SeriesOptions['key']) {
      this.chart.setSeries(null, { focus: true });
      if (this.focusedSeriesIndex) {
        this.chart.setSeries(this.focusedSeriesIndex, { show: false });
      }
      if (seriesKey) {
        const index = _.findIndex(this.chart.series, (series: uPlot.Series) => {
          return series.label === seriesKey;
        });
        if (index > 0 && this.chart.series[index].show) {
          this.focusedSeriesIndex = index + this.seriesCount;
          this.chart.setSeries(this.focusedSeriesIndex, { show: true, focus: true });
        }
      }
    },
  },
  unmounted() {
    clearInterval(this.setChartSizeInterval);
  },
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/main';
@use '../../assets/styles/colors' as colors;

.chart-wrapper {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 32px;
  justify-content: flex-end;
  position: relative;
}

/* uPlot elements */

:deep(.uplot) {
  font-family: var(--l11-font-sans);
}

:deep(.legend-wrapper) {
  display: flex;
  gap: 40px;
  width: 100%;

  .legend {
    align-items: center;
    display: flex;
    flex-flow: wrap;
    flex-grow: 1;
    gap: 10px;
    min-width: 0;
    padding: 0 32px;
  }

  .legend-item.single-item.hidden {
    background-color: map.get(colors.$colors, 'gallery');
    opacity: 0.5;
  }

  .vertical-divider {
    height: 20px;
    position: relative;
  }

  .legend-dropdown {
    align-self: auto;
    max-width: 100%;

    &.hidden .dropdown-header {
      background-color: map.get(colors.$colors, 'gallery');
      opacity: 0.5;
    }

    .dropdown-header {
      border: 0;
      max-height: none !important;
      min-height: auto;
      padding: 3px 4px 3px 1px;
      flex-wrap: wrap !important;
      justify-content: flex-start;
    }

    .dropdown-header:active {
      background: map.get(colors.$colors, 'selago');
    }

    .dropdown-header.open {
      background: map.get(colors.$colors, 'selago');
      border: 1px solid map.get(colors.$colors, 'fun-blue');
      padding: 2px 3px 2px 0;
    }

    .dropdown-list {
      border-top-width: 1px;
      max-width: 50vw;
      min-width: 100%;
      width: fit-content;
    }
  }

  .control-buttons {
    display: flex;
    flex-shrink: 0;
  }
}
</style>
